<template>
  <div class="form-group">
    <label for="inputId" aria-label="Date" class="form-label">
      {{ label }}
      <span v-if="optionalLabel" class="optional-label">({{ optionalLabel }})</span>
      <span v-if="required" class="required"></span>
    </label>
    <div
      :class="[`form-input`, `form-input--date`, `form-input--icon--right`, error && `form-input--error`]"
    >
      <icon class-name="form-input__icon" name="calendar" />
      <datepicker
        ref="datePicker"
        popup-class="maersk-date-picker-popup"
        data-test="datePicker"
        :type="type"
        :formatter="formatter"
        :value="value"
        :placeholder="placeholder"
        :input-attr="$attrs"
        :range="allowRange"
        :clearable="clearable"
        :disabled-date="disabledDate"
        :format="dateFormat"
        :disabled="disabled"
        input-class="form-input__field"
        :editable="editable"
        @input="changeSelectedDate($event)"
        @clear="clearDate"
      ></datepicker>
    </div>
    <p
      v-if="errorMessage"
      class="form-label form-label--error form-label--error--active"
    >{{ errorMessage }}</p>
    <div
      v-if="helpMessage"
      :class="[`form-help`, helpMessagePosition === `right` && `text-right`]"
    >{{ helpMessage }}</div>
  </div>
</template>

<script>
// Components
import Icon from "../Icon/Icon.vue";
import Datepicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  name: "DateInput",
  components: {
    Datepicker,
    Icon,
  },
  props: {
    /**
     * @description
     * select the type of picker date|datetime|year|month|time|week
     */
    type: {
      type: String,
      default: "date",
    },
    /**
     * @description
     * Label for Date Input
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Error Message for Date Input
     */
    errorMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Help Message for Date Input
     */
    helpMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Checks the Date Input help message position
     * Only Left and Right are possible
     */
    helpMessagePosition: {
      type: String,
      validator: (val) => ["left", "right"].includes(val),
      default: "left",
    },
    /**
     * @description
     * Provides Optional Label for Date Input
     */
    optionalLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Optional Label for Date Input
     */
    placeholder: {
      type: String,
      default: "Enter Date",
    },
    /**
     * @description
     * The date format to display in the field (NOT THE ONE WHICH IS RETURNED)
     */
    dateFormat: {
      type: String,
      default: "DD MMM YYYY",
    },
    /**
     * @description
     * Emits the value change on input, Parent Component needs to sync values emitted
     */
    value: {
      type: Date,
      default: null,
    },
    /**
     * @description
     * the first available date
     */
    minDate: {
      type: Date,
      default: null,
    },
    /**
     * @description
     * the last available date
     */
    maxDate: {
      type: Date,
      default: null,
    },
    /**
     * @description
     * you can use your own formatter, such as luxon.js
     * https://github.com/mengxiong10/vue2-datepicker#formatter
     */
    formatter: {
      type: Object,
      default: null,
    },
    /**
     * @description
     * Checks if the Date Input should be disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * A flag indicating if the clear text field is visible
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Date Input should display error outline
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * A flag indicating if date range is required
     */
    allowRange: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * A flag indicating if the datepicker input is editabale
     */
    editable: {
      type: Boolean,
      default: true,
    },
    required: {
      type: Boolean,
      default: false,
    }
  },
  methods: {
    /**
     * @description
     * emits change of the selected date
     */
    changeSelectedDate(value) {
      this.$emit("input", value);
    },
    clearDate(){
      this.$emit("clear");
    },
    /**
     * @description
     * displays valid dates in the dropdown uses Min & Max Date from the props
     */
    disabledDate(date) {
      let isMinDateCheckFailed = false;
      let isMaxDateCheckFailed = false;
      if (this.minDate) {
        isMinDateCheckFailed = date < this.minDate;
      }
      if (this.maxDate) {
        isMaxDateCheckFailed = date > this.maxDate;
      }
      return isMinDateCheckFailed || isMaxDateCheckFailed;
    },
  },
};
</script> 

<style lang="scss">
.mx-datepicker {
  width: 100%;
}

.mx-input {
  &:focus {
    outline: none;
  }
}

.mx-datepicker-popup.maersk-date-picker-popup {
  & .mx-calendar {
    max-width: 288px;
    min-height: 300px;
    width: 288px;
  }

  & .mx-calendar-header {
    & .mx-btn-text {
      vertical-align: top;
    }
  }

  & .mx-calendar-content {
    height: auto;
    width: auto;

    & .mx-table-date {
      border-collapse: separate;
      border-spacing: 2px;

      & th,
      & td {
        font-size: 16px;
        width: 36px;
      }

      & .cell {
        border: 1px solid transparent;

        &.today {
          font-weight: bold;
        }
      }
    }
  }
}

.mx-input-wrapper {
  & .mx-icon-calendar {
    display: none;
  }

  & .mx-icon-clear {
    right: 2.5rem;
  }
}

.mx-datepicker {
  & .mx-input {
    &:hover {
      border-color: var(--Gray-800);
    }
    &:focus {
      border-color: var(--Gray-800);
      box-shadow: 0 0 0 3px var(--Blue-50-Light);
    }
  }

  & .mx-icon-calendar {
    color: var(--Gray-900);
  }
}

.mx-datepicker-popup.maersk-date-picker-popup {
  border-radius: 4px;

  & .mx-calendar {
    font-family: "Maersk Text";
    color: var(--Gray-900);
  }

  & .mx-calendar-header {
    .mx-btn-text {
      font-family: "Maersk Text";
      color: var(--Gray-900);
    }
  }

  & .mx-calendar-content {
    & .mx-table-date {
      background-color: var(--Gray-50);

      & .mx-date-row {
        &.mx-active-week {
          .active,
          .active:focus {
            background-color: transparent;
            border: none;
          }
        }
      }

      & .cell {
        &.today:not(.active) {
          color: var(--Gray-900);
        }

        &:focus,
        &:hover:not(.disabled):not(.active) {
          color: var(--Gray-900);
          background-color: transparent;
          border: 1px solid var(--Blue-900-Darkest);
        }

        &.active,
        &.active:focus {
          background-color: var(--Blue-900-Darkest);
          border: 1px solid var(--Blue-900-Darkest);
        }
      }
    }
  }
}
</style>